import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useExpertTipFilters } from '@/hooks/useExpertTipFilters';
import { useExpertTips } from '@/hooks/useExpertTips';
import { useActiveExpertProfiles } from '@/hooks/useExpertProfiles';
import { useActiveTopics } from '@/hooks/useExpertTipTopics';
import ExpertTipsTable from '@/components/ExpertTipsTable';
import { Pagination } from '@/components/Pagination';
import type { ExpertTipFilters } from '@/types/expert-tip';

export default function ExpertTipsAdminPage() {
  const navigate = useNavigate();
  const { filters, updateFilter, clearFilters, hasActiveFilters } = useExpertTipFilters();
  const { data, isLoading, isError, error } = useExpertTips(filters);
  const { data: activeExperts } = useActiveExpertProfiles();
  const { data: activeTopics = [] } = useActiveTopics();
  const topicsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    document.title = 'Expert Tips | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (isError && error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load expert tips');
    }
  }, [isError, error]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (topicsRef.current && !topicsRef.current.contains(e.target as Node)) {
        topicsRef.current.open = false;
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTopicToggle = useCallback(
    (topicId: number) => {
      const current = filters.topicIds || [];
      const updated = current.includes(topicId)
        ? current.filter((id) => id !== topicId)
        : [...current, topicId];
      updateFilter('topicIds', updated);
    },
    [filters.topicIds, updateFilter]
  );

  const handleSort = useCallback(
    (column: ExpertTipFilters['sortBy']) => {
      if (filters.sortBy === column) {
        updateFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        updateFilter('sortBy', column);
        updateFilter('sortOrder', 'asc');
      }
    },
    [filters.sortBy, filters.sortOrder, updateFilter]
  );

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-pine-display font-display font-semibold text-pine-ink">Expert Tips</h1>
            <p className="text-pine-body text-pine-ink-soft mt-1">
              Manage expert tips for the Expert Tips feature
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/expert-tips/new')}
            className="admin-button-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span>New Expert Tip</span>
          </button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center gap-4">
          <select
            value={filters.expertId}
            onChange={(e) => updateFilter('expertId', e.target.value)}
            className="px-4 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink focus:border-pine-pine focus:outline-none transition-colors"
          >
            <option value="">All Experts</option>
            {activeExperts?.map((expert) => (
              <option key={expert.id} value={expert.id}>
                {expert.name}
              </option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value as ExpertTipFilters['status'])}
            className="px-4 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink focus:border-pine-pine focus:outline-none transition-colors"
          >
            <option value="">All Statuses</option>
            <option value="published">Published</option>
            <option value="unpublished">Unpublished</option>
            <option value="archived">Archived</option>
          </select>

          <div className="relative">
            <details ref={topicsRef} className="group">
              <summary className="cursor-pointer list-none px-4 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink flex items-center gap-2 focus:outline-none">
                <span>
                  {filters.topicIds.length > 0
                    ? `${filters.topicIds.length} topic${filters.topicIds.length > 1 ? 's' : ''}`
                    : 'All Topics'}
                </span>
                <svg
                  className="w-4 h-4 transition-transform group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="absolute z-10 mt-1 w-full min-w-[200px] max-h-60 overflow-y-auto bg-white border border-pine-line rounded-pine-input shadow-pine-card">
                {activeTopics.length === 0 ? (
                  <div className="p-3 text-center text-pine-ink-soft text-sm">
                    No topics available
                  </div>
                ) : (
                  activeTopics.map((topic) => (
                    <label
                      key={topic.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-pine-hover-item cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={filters.topicIds.includes(topic.id)}
                        onChange={() => handleTopicToggle(topic.id)}
                        className="w-4 h-4 rounded border-pine-line bg-white text-pine-pine focus:ring-1 focus:ring-pine-pine focus:ring-offset-0"
                      />
                      <span className="text-sm text-pine-ink">{topic.title}</span>
                    </label>
                  ))
                )}
              </div>
            </details>
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-pine-ink-soft hover:text-pine-ink underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </motion.div>

      {/* Error State */}
      {isError && (
        <div className="admin-card p-6 border border-red-500/20 bg-red-500/5">
          <p className="text-red-400">Failed to load expert tips. Please try again.</p>
        </div>
      )}

      {/* Table */}
      {!isError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <ExpertTipsTable
            tips={data?.items || []}
            isLoading={isLoading}
            isEmpty={!isLoading && data?.total === 0 && !hasActiveFilters}
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
            onSort={handleSort}
          />
        </motion.div>
      )}

      {/* Pagination */}
      {!isError && data && totalPages > 1 && (
        <Pagination
          currentPage={filters.page}
          totalPages={totalPages}
          totalItems={data.total}
          pageSize={filters.pageSize}
          onPageChange={(page) => updateFilter('page', page)}
        />
      )}
    </div>
  );
}
